import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { Session } from '@/types'
import { supabase } from '../../../lib/supabase'

type ActionType =
    | 'sent_invitation'
    | 'granted'
    | 'accepted'
    | 'rejected'
    | 'removed'
    | 'access_requested'
    | 'forgotten'

interface HistoryEntry {
    id: string
    created_at: string
    action: ActionType
    message: string | null
    target_email?: string
    biblionaut?: {
        full_name?: string | null
        email?: string
    }
    owner_profile?: {
        full_name?: string | null
        email?: string
    }
}

interface HistoryTableProps {
    session: Session
    mode?: 'owner' | 'biblionaut'
}

export default function HistoryTable({ session, mode = 'owner' }: Readonly<HistoryTableProps>) {
    const { t, i18n } = useTranslation()
    const [history, setHistory] = useState<HistoryEntry[]>([])
    const [loading, setLoading] = useState(true)

    const fetchHistory = useCallback(async () => {
        try {
            let query = supabase
                .from('library_share_history')
                .select(mode === 'owner' ? '*, biblionaut:biblionaut_id(full_name, email)' : '*, owner_profile:owner_id(full_name, email)')
                .order('created_at', { ascending: false })

            if (mode === 'owner') {
                query = query.eq('owner_id', session.user.id)
            } else {
                query = query.eq('biblionaut_id', session.user.id)
            }

            const { data, error } = await query

            if (error) throw error
            setHistory(data as HistoryEntry[])
        } catch (error) {
            console.error('Error fetching history:', error)
        } finally {
            setLoading(false)
        }
    }, [mode, session.user.id])

    useEffect(() => {
        fetchHistory()
    }, [fetchHistory])

    const formatDate = (isoString: string) => {
        return new Date(isoString).toLocaleString(i18n.language, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    const getActionLabel = (action: ActionType | string) => {
        if (['sent_invitation', 'granted', 'accepted', 'rejected', 'removed', 'access_requested', 'forgotten'].includes(action)) {
            return t(`biblionautas.history.actions.${action}`)
        }
        return action
    }

    const getCounterpartName = (entry: HistoryEntry) => {
        if (mode === 'owner') {
            return entry.biblionaut?.email || entry.target_email || t('biblionautas.history.terms.unknown')
        } else {
            return entry.owner_profile?.email || t('biblionautas.history.terms.library')
        }
    }

    return (
        <div className="history-section" style={{ marginTop: '2rem' }}>
            <h4>{t('biblionautas.history.title')}</h4>
            {(() => {
                if (loading) return <p>{t('biblionautas.history.loading')}</p>
                if (history.length === 0) return <p className="text-muted">{t('biblionautas.history.empty')}</p>
                return (
                    <div className="table-responsive">
                        <table className="history-table">
                            <thead>
                                <tr>
                                    <th>{t('biblionautas.history.headers.date')}</th>
                                    <th>{mode === 'owner' ? t('biblionautas.history.headers.biblionaut') : t('biblionautas.history.headers.library')}</th>
                                    <th>{t('biblionautas.history.headers.action')}</th>
                                    <th>{t('biblionautas.history.headers.message')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((entry) => (
                                    <tr key={entry.id}>
                                        <td>{formatDate(entry.created_at)}</td>
                                        <td>{getCounterpartName(entry)}</td>
                                        <td>
                                            <span className={`badge-action ${entry.action}`}>{getActionLabel(entry.action)}</span>
                                        </td>
                                        <td className="msg-cell" title={entry.message || ''}>
                                            {entry.message || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            })()}

            <style>{`
        .table-responsive {
          overflow-x: auto;
          background: var(--bg-secondary);
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }
        .history-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9rem;
          min-width: 600px;
        }
        .history-table th,
        .history-table td {
          padding: 1rem;
          text-align: left;
          border-bottom: 1px solid var(--border-color);
        }
        .history-table th {
          background: rgba(0, 0, 0, 0.05);
          font-weight: 600;
          color: var(--text-primary);
        }
        .history-table tr:last-child td {
          border-bottom: none;
        }
        .msg-cell {
          max-width: 250px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          cursor: help;
        }
        .badge-action {
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.8rem;
          font-weight: 500;
          white-space: nowrap;
        }
        .badge-action.granted,
        .badge-action.accepted {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }
        .badge-action.rejected,
        .badge-action.removed {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }
        .badge-action.sent_invitation,
        .badge-action.access_requested {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }
      `}</style>
        </div>
    )
}
